import { useRef, useEffect, useState } from 'react';
import { ModerationBadgeState, type ModerationBadgeResult } from './useModerationBadgeState';

const APPROVED_STATES = new Set<ModerationBadgeState>([
  ModerationBadgeState.AI_APPROVED,
  ModerationBadgeState.MODERATOR_APPROVED,
  ModerationBadgeState.APPEAL_APPROVED
]);

const DISMISS_DELAY = 7000; // 7 seconds — matches APPROVAL_DISMISS_DELAY in ModerationStatusContext
const FADEOUT_DURATION = 600; // ms — matches CSS animation duration

interface ApprovalTransitionResult {
  /** True when a non-approved -> approved transition was just detected */
  justApproved: boolean;
  /** True during the fade-out animation before justApproved becomes false */
  isDismissing: boolean;
  /** The specific approved state that was transitioned to */
  approvedState: ModerationBadgeState | null;
  /** Call to acknowledge/dismiss the transition */
  dismiss: () => void;
}

/**
 * Tracks previous badge state and detects when it transitions from a
 * non-approved state to an approved state. Skips first render to avoid
 * firing on initial page load. Auto-dismisses after DISMISS_DELAY ms.
 *
 * KCOMM-815: Transient Approval Notification
 */
export function useModerationApprovalTransition(
  badgeResult: ModerationBadgeResult | null
): ApprovalTransitionResult {
  const previousStateRef = useRef<ModerationBadgeState | null | undefined>();
  const isFirstRenderRef = useRef(true);
  const [justApproved, setJustApproved] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);
  const [approvedState, setApprovedState] = useState<ModerationBadgeState | null>(null);

  const currentState = badgeResult?.state ?? null;

  useEffect(() => {
    // Skip first render -- don't fire on initial page load
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      previousStateRef.current = currentState;
      return;
    }

    const previousState = previousStateRef.current;
    const wasApproved = previousState != null && APPROVED_STATES.has(previousState);
    const isNowApproved = currentState != null && APPROVED_STATES.has(currentState);

    // Transition: was NOT approved -> is NOW approved
    if (!wasApproved && isNowApproved) {
      setJustApproved(true);
      setIsDismissing(false);
      setApprovedState(currentState);
    } else if (justApproved && !isNowApproved) {
      // Status reverted (approved -> non-approved) during dismiss window — clear immediately
      setJustApproved(false);
      setIsDismissing(false);
      setApprovedState(null);
    }

    previousStateRef.current = currentState;
  }, [currentState, justApproved]);

  // Start fade-out after visibility delay, then fully remove after animation
  useEffect(() => {
    if (!justApproved || isDismissing) {
      return;
    }
    const fadeTimer = setTimeout(() => {
      setIsDismissing(true);
    }, DISMISS_DELAY);
    return () => clearTimeout(fadeTimer);
  }, [justApproved, isDismissing]);

  useEffect(() => {
    if (!isDismissing) {
      return;
    }
    const removeTimer = setTimeout(() => {
      setJustApproved(false);
      setIsDismissing(false);
      setApprovedState(null);
    }, FADEOUT_DURATION);
    return () => clearTimeout(removeTimer);
  }, [isDismissing]);

  const dismiss = () => {
    setIsDismissing(true);
  };

  return { justApproved, isDismissing, approvedState, dismiss };
}

export { APPROVED_STATES };
export default useModerationApprovalTransition;
